import { HvlPage, HvlResponse } from '@hvlng/framework-bff/core';
import { JsonConvert } from 'json2typescript';
import { Observable } from 'rxjs';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { map } from 'rxjs/operators';
import { FileOwnershipModel, HrConfigurationService } from 'humanresources-common-shared';
import { PerPersonnelModel } from '../model/per-personnel-model';
import { PerPersonnelQueryModel } from '../model/per-personnel-query-model';
import { PerPersonnelSearchQueryModel } from '../model/hr-per-personnel-search-query-model';
import { PerPersonnelSearchModel } from '../model/hr-per-personnel-search-model';
import { LookupModel } from 'infrastructure-shared';
import { PersonnelManagerInfoModel } from '../model/personnel-manager-info-model';
import { PerEntityInfoModel } from '../model/per-entity-info-model';
import { PersonnelBasisInfoCardModel } from '../model/personnel-basis-info-card-model';
import {
    ObjectCardDetailFieldModel,
} from '../../organization/object-management/object-definition/model/object-card-detail-field-model';
import { PersonnelSessionComparisonResultModel } from '../model/personnel-session-comparison-result-model';

@Injectable({
    providedIn: 'root',
})
export class PerPersonnelService {
    rootUrl: string;

    constructor(
        private httpClient: HttpClient,
        private configService: HrConfigurationService,
    ) {
        this.rootUrl = configService.config.hr.personnel + '/personnelService/personnel/';
    }

    public getList(): Observable<Array<PerPersonnelModel>> {
        return this.httpClient.get<HvlResponse<Array<PerPersonnelModel>>>(this.rootUrl).pipe(
            map((data) => {
                const converter = new JsonConvert();
                return converter.deserializeArray(data.body || [], PerPersonnelModel);
            }),
        );
    }

    public queryPersonnelPage(pagination: PerPersonnelQueryModel): Observable<HvlPage<Array<PerPersonnelModel>>> {
        return this.queryPage(pagination);
    }
    public searchPersonnelQueryList(
        queryModel: PerPersonnelSearchQueryModel,
    ): Observable<Array<PerPersonnelSearchModel>> {
        return this.searchPersonnelQueryListA(queryModel);
    }
    public searchPersonnelQueryListA(
        queryModel: PerPersonnelSearchQueryModel,
    ): Observable<Array<PerPersonnelSearchModel>> {
        return this.httpClient
            .post<HvlResponse<Array<PerPersonnelSearchModel>>>(this.rootUrl + 'searchPersonnelQuery', queryModel)
            .pipe(
                map((data) => {
                    const converter = new JsonConvert();
                    return converter.deserializeArray(data.body || [], PerPersonnelSearchModel);
                }),
            );
    }
    public queryPage(pagination: PerPersonnelQueryModel): Observable<HvlPage<Array<PerPersonnelModel>>> {
        return this.httpClient.post<HvlResponse<HvlPage<PerPersonnelModel[]>>>(this.rootUrl + 'page', pagination).pipe(
            map((hvlpage) => {
                const converter = new JsonConvert();
                if (hvlpage.body?.totalElements === 0) {
                    return new HvlPage<Array<PerPersonnelModel>>(
                        [],
                        hvlpage.body.totalElements,
                        hvlpage.body.totalPages,
                        hvlpage.errorDetail,
                    );
                }
                const model = converter.deserializeArray(hvlpage.body.data, PerPersonnelModel);
                return new HvlPage<Array<PerPersonnelModel>>(
                    model,
                    hvlpage.body.totalElements,
                    hvlpage.body.totalPages,
                    hvlpage.errorDetail,
                );
            }),
        );
    }

    public activePersonnelQueryPage(pagination: PerPersonnelQueryModel): Observable<HvlPage<Array<PerPersonnelModel>>> {
        return this.httpClient
            .post<HvlResponse<HvlPage<PerPersonnelModel[]>>>(this.rootUrl + 'activePersonnelPage', pagination)
            .pipe(
                map((hvlpage) => {
                    const converter = new JsonConvert();
                    if (hvlpage.body?.totalElements === 0) {
                        return new HvlPage<Array<PerPersonnelModel>>(
                            [],
                            hvlpage.body.totalElements,
                            hvlpage.body.totalPages,
                            hvlpage.errorDetail,
                        );
                    }
                    const model = converter.deserializeArray(hvlpage.body.data, PerPersonnelModel);
                    return new HvlPage<Array<PerPersonnelModel>>(
                        model,
                        hvlpage.body.totalElements,
                        hvlpage.body.totalPages,
                        hvlpage.errorDetail,
                    );
                }),
            );
    }

    public queryList(queryModel: PerPersonnelQueryModel): Observable<Array<PerPersonnelModel>> {
        return this.httpClient.post<HvlResponse<Array<PerPersonnelModel>>>(this.rootUrl + 'query', queryModel).pipe(
            map((data) => {
                const converter = new JsonConvert();
                return converter.deserializeArray(data.body || [], PerPersonnelModel);
            }),
        );
    }
    public genericQueryBuilder(queryModel: any): Observable<Array<PerPersonnelModel>> {
        return this.httpClient
            .post<HvlResponse<Array<PerPersonnelModel>>>(this.rootUrl + 'genericQueryBuilder', queryModel)
            .pipe(
                map((data) => {
                    const converter = new JsonConvert();
                    return converter.deserializeArray(data.body || [], PerPersonnelModel);
                }),
            );
    }

    public activePersonnelQueryList(queryModel: PerPersonnelQueryModel): Observable<Array<PerPersonnelModel>> {
        return this.httpClient
            .post<HvlResponse<Array<PerPersonnelModel>>>(this.rootUrl + 'activePersonnelQuery', queryModel)
            .pipe(
                map((data) => {
                    const converter = new JsonConvert();
                    return converter.deserializeArray(data.body || [], PerPersonnelModel);
                }),
            );
    }

    public getPersonnelInformation(): Observable<PerPersonnelModel> {
        return this.httpClient.post<HvlResponse<PerPersonnelModel>>(this.rootUrl + 'getPersonnelInformation', {}).pipe(
            map((data) => {
                const converter = new JsonConvert();
                if (data.body == null) {
                    return new PerPersonnelModel();
                }
                return converter.deserializeObject(data.body, PerPersonnelModel);
            }),
        );
    }

    public getByPersonnelId(id: number): Observable<PerPersonnelModel> {
        return this.httpClient.get<HvlResponse<PerPersonnelModel>>(this.rootUrl + 'getPersonnel/' + id).pipe(
            map((data) => {
                const converter = new JsonConvert();
                if (data.body == null) {
                    return new PerPersonnelModel();
                }
                return converter.deserializeObject(data.body, PerPersonnelModel);
            }),
        );
    }
    public isManagerByLoggedInPersonnel(): Observable<Boolean> {
        return this.httpClient.get<HvlResponse<Boolean>>(this.rootUrl + 'isManagerByLoggedInPersonnel/').pipe(
            map((data) => {
                const converter = new JsonConvert();
                if (data.body == null) {
                    return false;
                }
                return data.body;
            }),
        );
    }
    public getByIdentityIdentificationNumber(identificationNumber: string): Observable<Array<PerPersonnelModel>> {
        return this.httpClient
            .get<
                HvlResponse<Array<PerPersonnelModel>>
            >(this.rootUrl + 'getByIdentityIdentificationNumber/' + identificationNumber)
            .pipe(
                map((data) => {
                    const converter = new JsonConvert();
                    return converter.deserializeArray(data.body || [], PerPersonnelModel);
                }),
            );
    }

    public getImmediateManagerById(id: number): Observable<PersonnelManagerInfoModel> {
        return this.httpClient.get<HvlResponse<PerPersonnelModel>>(this.rootUrl + 'immediateManager/' + id).pipe(
            map((data) => {
                const converter = new JsonConvert();
                if (data.body == null) {
                    return new PersonnelManagerInfoModel();
                }
                return converter.deserializeObject(data.body, PersonnelManagerInfoModel);
            }),
        );
    }

    public update(model: PerPersonnelModel): Observable<PerPersonnelModel> {
        const converter = new JsonConvert();
        return this.httpClient.put<HvlResponse<PerPersonnelModel>>(this.rootUrl, converter.serialize(model)).pipe(
            map((data) => {
                return converter.deserializeObject(data.body, PerPersonnelModel);
            }),
        );
    }

    public updateForRegistryNumberAndStatus(model: PerPersonnelModel): Observable<PerPersonnelModel> {
        const converter = new JsonConvert();

        return this.httpClient
            .post<
                HvlResponse<PerPersonnelModel>
            >(this.rootUrl + 'updateForRegistryNumberAndStatus', converter.serialize(model, PerPersonnelModel))
            .pipe(
                map((data) => {
                    return data.body;
                }),
            );
    }

    public create(model: PerPersonnelModel): Observable<PerPersonnelModel> {
        const converter = new JsonConvert();

        return this.httpClient
            .post<HvlResponse<PerPersonnelModel>>(this.rootUrl, converter.serialize(model, PerPersonnelModel))
            .pipe(
                map((data) => {
                    return data.body;
                }),
            );
    }

    public deleteById(id: number): Observable<any> {
        return this.httpClient.delete(this.rootUrl + id);
    }

    public addPicture(file: File, peopleId?: number): Observable<FileOwnershipModel> {
        let formData: FormData = new FormData();
        formData.append('picture', file, file.name);

        let headers = new HttpHeaders();
        headers.append('Content-Type', 'multipart/form-data');
        headers.append('Accept', 'application/json');

        return this.httpClient
            .post<
                HvlResponse<FileOwnershipModel>
            >(this.rootUrl + 'addPicture/' + (peopleId ?? ''), formData, { headers: headers })
            .pipe(
                map((data) => {
                    const converter = new JsonConvert();
                    return converter.deserializeObject(data.body, FileOwnershipModel);
                }),
            );
    }

    public getPictureUrl(peopleId: number): string {
        return this.rootUrl + 'getPicture/' + peopleId;
    }

    public removePicture(peopleId: number): Observable<any> {
        return this.httpClient.delete(this.rootUrl + 'removePicture/' + peopleId);
    }

    public getPersonnelLookupList(queryModel: PerPersonnelQueryModel): Observable<Array<LookupModel>> {
        return this.httpClient
            .post<HvlResponse<Array<LookupModel>>>(this.rootUrl + 'personnelLookupList', queryModel)
            .pipe(
                map((data) => {
                    const converter = new JsonConvert();
                    return converter.deserializeArray(data.body || [], LookupModel);
                }),
            );
    }

    public getAllManagerPersonnelFromOrgGeneralInfo(): Observable<Array<LookupModel>> {
        return this.httpClient
            .get<HvlResponse<Array<LookupModel>>>(this.rootUrl + 'getAllManagerPersonnelFromOrgGeneralInfo')
            .pipe(
                map((data) => {
                    const converter = new JsonConvert();
                    if (data.body == null) {
                        return new Array<LookupModel>();
                    }
                    return converter.deserializeArray(data.body || [], LookupModel);
                }),
            );
    }

    public isManagedByLoggedInPersonnel(id: number): Observable<any> {
        return this.httpClient.get<HvlResponse<any>>(this.rootUrl + 'isManagedByLoggedInPersonnel/' + id).pipe(
            map((data) => {
                const converter = new JsonConvert();
                return data.body;
            }),
        );
    }
    public isAdminOrManagerOrHasAuthority(): Observable<any> {
        return this.httpClient.get<HvlResponse<any>>(this.rootUrl + 'isAdminOrManagerOrHasAuthority/').pipe(
            map((data) => {
                const converter = new JsonConvert();
                return data.body;
            }),
        );
    }
    public getPersonnelEmptyCadreReport(data): Observable<any> {
        return this.httpClient
            .post(this.rootUrl + 'getPersonnelEmptyCadreReport/', data, { responseType: 'blob' })
            .pipe(
                map((data) => {
                    return data;
                }),
            );
    }
    public getPersonnelCadreRegisterReport(data): Observable<any> {
        return this.httpClient
            .post(this.rootUrl + 'getPersonnelCadreRegisterReport/', data, { responseType: 'blob' })
            .pipe(
                map((data) => {
                    return data;
                }),
            );
    }

    readSettingFile(name: string): Promise<any> {
        return this.httpClient.get<any>(name).toPromise();
    }

    public getEntityInfo(): Observable<Array<PerEntityInfoModel>> {
        return this.httpClient.get<HvlResponse<Array<PerEntityInfoModel>>>(this.rootUrl + 'getEntityInfo/').pipe(
            map((data) => {
                const converter = new JsonConvert();
                return converter.deserializeArray(data.body || [], PerEntityInfoModel);
            }),
        );
    }

    public getByPersonnelIdSpecificDate(queryModel: PerPersonnelQueryModel): Observable<PerPersonnelModel> {
        const converter = new JsonConvert();
        return this.httpClient
            .post<
                HvlResponse<PerPersonnelModel>
            >(this.rootUrl + 'getByPersonnelIdSpecificDate', converter.serialize(queryModel))
            .pipe(
                map((data) => {
                    return converter.deserializeObject(data.body, PerPersonnelModel);
                }),
            );
    }

    public getPersonnelBasisInfoCardData(id: number): Observable<PersonnelBasisInfoCardModel> {
        return this.httpClient
            .get<HvlResponse<PersonnelBasisInfoCardModel>>(this.rootUrl + 'getPersonnelBasisInfoCardData/' + id)
            .pipe(
                map((data) => {
                    const converter = new JsonConvert();
                    if (data.body == null) {
                        return new PersonnelBasisInfoCardModel();
                    }
                    return converter.deserializeObject(data.body, PersonnelBasisInfoCardModel);
                }),
            );
    }
    public getPersonnelCardDetails(id: number): Observable<ObjectCardDetailFieldModel> {
        const pattern = 'getPersonnelCardDetails/' + id;
        const serviceUrl = this.rootUrl + pattern; // Full path
        return this.httpClient.get<HvlResponse<ObjectCardDetailFieldModel>>(serviceUrl).pipe(
            map((data) => {
                const converter = new JsonConvert();
                return converter.deserializeObject(data.body, ObjectCardDetailFieldModel);
            }),
        );
    }

    public compareWithSessionPersonnel(peopleId: number): Observable<PersonnelSessionComparisonResultModel> {
        return this.httpClient.get<HvlResponse<PersonnelSessionComparisonResultModel>>(this.rootUrl + 'compareWithSessionPersonnel/' + peopleId).pipe(
            map((data) => {
                const converter = new JsonConvert();
                return converter.deserializeObject(data.body, PersonnelSessionComparisonResultModel);
            }),
        );
    }

}
